import { Shield, Skull, Sparkles, Star, Sword } from "lucide-react";
import Head from "next/head";
import LandingPage from "@/components/organisms/GeneratorLandingPage";
const features = [{
  icon: <Skull className="w-6 h-6 text-white" />,
  title: "Monster Generation",
  description: "Create terrifying and awe-inspiring monsters instantly with our AI-powered art generation."
}, {
  icon: <Shield className="w-6 h-6 text-white" />,
  title: "Customizable Designs",
  description: "Adjust every aspect of your monsters, from their appearance to their environment and pose."
}, {
  icon: <Sparkles className="w-6 h-6 text-white" />,
  title: "Endless Variety",
  description: "Generate unique variations of monsters to create diverse and memorable encounters."
}];
const whyUseReasons = [{
  icon: <Skull className="w-5 h-5 text-white" />,
  title: "Unique Encounters",
  description: "Create memorable monsters that your players have never seen before, making each encounter special."
}, {
  icon: <Sparkles className="w-5 h-5 text-white" />,
  title: "Quick Creation",
  description: "Save hours of time searching for the perfect monster art with instant generation."
}, {
  icon: <Shield className="w-5 h-5 text-white" />,
  title: "Campaign Consistency",
  description: "Maintain a consistent art style across all your campaign's monsters."
}, {
  icon: <Star className="w-5 h-5 text-white" />,
  title: "Visual Impact",
  description: "Make your encounters more engaging with stunning visual representations of your monsters."
}];
const howToSteps = [{
  title: "Choose monster type",
  description: "Select the category of monster you want to create (aberration, dragon, undead, etc.)."
}, {
  title: "Describe features",
  description: "Detail the monster's unique characteristics, size, and distinctive features."
}, {
  title: "Set the scene",
  description: "Describe the environment and pose to create the perfect atmosphere for your monster."
}, {
  title: "Generate variations",
  description: "Create multiple versions to find the perfect representation of your monster."
}, {
  title: "Fine-tune details",
  description: "Adjust the final image to perfect the monster's appearance and atmosphere."
}];
const promptTips = [{
  icon: <Skull className="w-5 h-5 text-white" />,
  title: "Focus on unique features",
  description: "Highlight what makes your monster special - unusual appendages, markings, or abilities."
}, {
  icon: <Shield className="w-5 h-5 text-white" />,
  title: "Consider scale",
  description: "Include size references to convey how imposing or subtle your monster appears."
}, {
  icon: <Sword className="w-5 h-5 text-white" />,
  title: "Add environmental context",
  description: "Describe the monster's habitat or preferred hunting grounds to enhance the image."
}, {
  icon: <Star className="w-5 h-5 text-white" />,
  title: "Include action",
  description: "Describe dynamic poses or actions to make your monster feel alive and threatening."
}, {
  icon: <Sparkles className="w-5 h-5 text-white" />,
  title: "Detail special effects",
  description: "Mention any magical auras, elemental effects, or supernatural qualities."
}];
const examplePrompts = [{
  icon: <Skull className="w-5 h-5 text-white" />,
  class: "Dragon",
  description: "An ancient crystal dragon with translucent scales that refract light, perched atop a mountain of precious gems, its wings spread majestically against a stormy sky."
}, {
  icon: <Shield className="w-5 h-5 text-white" />,
  class: "Aberration",
  description: "A writhing mass of tentacles and eyes, emerging from a portal of swirling cosmic energy, its form defying natural laws."
}, {
  icon: <Sword className="w-5 h-5 text-white" />,
  class: "Undead",
  description: "A ghostly knight still mounted on its skeletal warhorse, trailing ethereal blue flames as it charges through a misty battlefield."
}, {
  icon: <Star className="w-5 h-5 text-white" />,
  class: "Beast",
  description: "A massive dire wolf with frost-covered fur and glowing blue eyes, stalking through a snow-covered forest at twilight."
}, {
  icon: <Sparkles className="w-5 h-5 text-white" />,
  class: "Elemental",
  description: "A towering earth elemental formed from crystal and precious metals, its core glowing with inner fire as it rises from the ground."
}];
const galleryImages = [{
  src: "https://charsheet-gen.s3.eu-west-2.amazonaws.com/charsheet-gen/66f00f82-8f1e-4e6d-9e5b-0ea3447a7ce7.webp",
  title: "Naga",
  description: "A Dungeons and Dragons Naga standing tall in a fantasy setting.",
  link: "/generate?category=monsters"
}, {
  src: "https://charsheet-gen.s3.eu-west-2.amazonaws.com/charsheet-gen/99780fca-6775-4f0a-8d04-e9b0b3acf374.webp",
  title: "Owlbear",
  description: "An Owlbear standing in a forest ready to charge.",
  link: "/generate?category=monsters"
}, {
  src: "https://charsheet-gen.s3.eu-west-2.amazonaws.com/charsheet-gen/a596543c-c34f-46d8-bd72-2d9080a4f43a.webp",
  title: "Hill Giant",
  description: "A Hill Giant standing holding a giant mace.",
  link: "/generate?category=monsters"
}];
export default function MonsterArtPage() {
  return <>
            <Head data-sentry-element="Head" data-sentry-source-file="index.tsx">
                <title>
                    The Best Free DnD AI Monster Generator - Create Fantasy Art
                    for D&D
                </title>
                <meta name="description" content="Try our free DnD AI Monster Generator to create monster art for your Dungeons & Dragons campaigns." data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:title" content="The Best Free DnD AI Monster Generator - Create Fantasy Art
                    for D&D" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:description" content="Try our free DnD AI Character Generator to create monster art for your Dungeons & Dragons campaigns." data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <link rel="canonical" href="https://char-gen.com/generation/dnd-monster" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "CharGen",
          url: "https://char-gen.com/generation/dnd-monster",
          description: "The Best Free AI DnD Monster Generator - Create Free AI Monster Art for Your Campaigns"
        })
      }} />
            </Head>
            <LandingPage title="DnD Monster Art" subtitle="Create awe-inspiring monsters for your D&D campaigns. From terrifying dragons to mysterious aberrations, bring your deadliest encounters to life with AI-powered art generation." mainIcon={Skull} features={features} whyUseTitle="Why Use a Monster Art Generator?" whyUseSubtitle="Create unique and memorable monsters that will challenge and surprise your players." whyUseReasons={whyUseReasons} howToTitle="How To Create Monster Art" howToSubtitle="Learn how to generate the perfect monster artwork for your campaign." howToSteps={howToSteps} promptTipsTitle="Writing Effective Monster Prompts" promptTipsSubtitle="Create prompts that bring your monsters to life." promptTips={promptTips} examplePromptsTitle="Example Monster Prompts" examplePromptsSubtitle="Get inspired with these monster art prompts:" examplePrompts={examplePrompts} galleryImages={galleryImages} ctaTitle="Ready to Create Your Monsters?" ctaSubtitle="Transform your monster concepts into stunning artwork with our AI-powered generator." ctaButtonText="Start Generating Monsters" finalCtaTitle="Unleash Your Creativity" finalCtaSubtitle="Join thousands of DMs creating unforgettable monsters for their campaigns. Start your creative journey today!" data-sentry-element="LandingPage" data-sentry-source-file="index.tsx" />
        </>;
}